import { useCallback, useMemo } from 'react';
import { Asset, ASSET_TYPE, Broadcast, Channel } from '@24i/nxg-sdk-photon';
import { isWithinInterval } from 'date-fns';

import { useEpgCache } from '../../../hooks/query/epg/useEpgCache';

export interface IChannel {
    channel: Channel;
    isCurrentlyPlaying: boolean;
    currentProgram?: Broadcast;
    allowsRestart?: boolean;
}

export interface IEpgData {
    channels: IChannel[];
}

export const useEpgDataManager = (asset?: Asset | null): IEpgData | undefined => {
    const date = new Date();
    const startTime = date.setMinutes(0, 0, 0);
    const endTime = new Date().setHours(date.getHours() + 1, 0, 0, 0);

    const { allChannels, data } = useEpgCache({ startTime, endTime });

    return useMemo(() => {
        const isLiveAsset =
            asset?.type === ASSET_TYPE.CHANNEL ||
            (asset?.isLive && [ASSET_TYPE.EPG, ASSET_TYPE.BROADCAST].includes(asset.type));
        if (!asset || !data || !isLiveAsset) return undefined;
        const channels = data
            .map((fetchedChannel): IChannel => {
                let channelId;
                if (asset.type === ASSET_TYPE.BROADCAST) {
                    channelId = asset.channelId || (asset as any).broadcastMetadata?.channelId;
                } else if (asset.type === ASSET_TYPE.CHANNEL) {
                    channelId = asset.id || (asset as any).broadcastMetadata?.channelId;
                }
                return {
                    channel: fetchedChannel,
                    // TODO: `isCurrentlyPlaying` needs a better check. For now it's using the EpgDataChannel data struct.

                    isCurrentlyPlaying: channelId === fetchedChannel.id,
                    currentProgram: fetchedChannel?.programs?.find(
                        (program) => program.start <= Date.now() && program.end > Date.now()
                    ),
                    allowsRestart: fetchedChannel.allowsRestart ?? true,
                };
            })
            .sort((channelA, channelB) => channelA.channel.number - channelB.channel.number);

        if (channels.length === 0) {
            return undefined;
        }

        return { channels };
    }, [allChannels, data, asset]);
};

export const useEpgDataFilteredForNow = () =>
    useCallback((epgDataToFilter: IEpgData | undefined) => {
        if (!epgDataToFilter || epgDataToFilter.channels.length === 0) {
            return undefined;
        }

        const finalPrograms: Broadcast[] = [];
        epgDataToFilter.channels.forEach((channel) => {
            const programs = channel?.channel?.programs?.filter((p) => p) || [];
            const currentProgram = programs.find((program) =>
                isWithinInterval(Date.now(), { start: program.start, end: program.end })
            );
            if (currentProgram) finalPrograms.push(currentProgram);
        });

        return finalPrograms;
    }, []);
